import React, { useRef, useEffect } from 'react'
import { MessageInfo, SessionType } from '@/api/types/chat'

interface MessageContextMenuProps {
  message: MessageInfo
  sessionType: SessionType
  isShowPin: boolean,
  position: { x: number; y: number }
  onClose: () => void
  onPin: (messageId: string) => void
  onTranslate: (messageId: string) => void
  onCopy?: (content: string) => void
}

/**
 * 消息右键菜单组件
 */
export default function MessageContextMenu({
  message,
  sessionType,
  isShowPin,
  position,
  onClose,
  onPin,
  onTranslate,
  onCopy,
}: MessageContextMenuProps): React.JSX.Element {
  const menuRef = useRef<HTMLDivElement>(null)

  // 点击外部区域关闭菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    // 短暂延迟后绑定事件，避免立即触发
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onClose])

  // 按ESC键关闭菜单
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // 计算菜单位置，确保不超出视窗
  const calculatePosition = () => {
    const rect = { width: 200, height: 300 } // 预估菜单尺寸
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    let x = position.x
    let y = position.y

    // 防止右侧超出
    if (x + rect.width > viewportWidth) {
      x = viewportWidth - rect.width - 10
    }

    // 防止底部超出
    if (y + rect.height > viewportHeight) {
      y = viewportHeight - rect.height - 10
    }

    // 防止左侧超出
    if (x < 10) {
      x = 10
    }

    // 防止顶部超出
    if (y < 10) {
      y = 10
    }

    return { x, y }
  }

  const menuPosition = calculatePosition()

  // 菜单项配置
  const menuItems = [
    // 只有群聊才显示置顶功能
    ...(sessionType === SessionType.Group && isShowPin ? [{
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
        </svg>
      ),
      label: '置顶消息',
      onClick: () => {
        onPin(message.message_id)
        onClose()
      }
    }] : []),
    {
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
        </svg>
      ),
      label: '翻译',
      onClick: () => {
        onTranslate(message.message_id)
        onClose()
      }
    },
    // 复制功能
    ...(onCopy ? [{
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
      label: '复制',
      onClick: () => {
        onCopy(message.decryptedContentData?.content || '')
        onClose()
      }
    }] : [])
  ]

  return (
    <>
      {/* 遮罩层 */}
      <div className="fixed inset-0 z-50" />
      
      {/* 菜单内容 */}
      <div
        ref={menuRef}
        className="fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-48"
        style={{
          left: menuPosition.x,
          top: menuPosition.y
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {menuItems.map((item, index) => (
          <button
            key={index}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-3 transition-colors"
            onClick={item.onClick}
          >
            <span className="text-gray-500">{item.icon}</span>
            <span>{item.label}</span>
          </button>
        ))}
        
        {menuItems.length === 0 && (
          <div className="px-4 py-2 text-sm text-gray-500">
            暂无可用操作
          </div>
        )}
      </div>
    </>
  )
}
